/**
 * Dashboard route access by subscription tier.
 * 0 = free | 1 = basic (50₪) | 2 unused (legacy professional mapped to 3)
 * | 3 = professional (100₪, includes 80₪ legacy) | 4 = wholesale (120₪)
 */

export const PLAN_PRICES_ILS = {
  basic: 50,
  professional: 100,
  advanced: 100,
  wholesale: 120,
};

/** Legacy USD prices — existing subscribers keep tier mapping. */
const LEGACY_PRICE_TIER = {
  10: 1,
  15: 3, // old professional ($15) merged into professional
  25: 3,
  45: 4,
};

const ILS_PRICE_TIER = {
  50: 1,
  80: 3, // old professional (80₪) merged into professional
  100: 3,
  120: 4,
  150: 4,
};

export function tierFromPlanPrice(price) {
  const p = Number(price);
  if (!Number.isFinite(p)) return null;
  if (ILS_PRICE_TIER[p] != null) return ILS_PRICE_TIER[p];
  if (LEGACY_PRICE_TIER[p] != null) return LEGACY_PRICE_TIER[p];
  return null;
}

const BASE_PREFIXES = [
  "/dashboard",
  "/settings",
  "/catalog-studio",
  "/catalog-builder",
  "/catalog-editor",
  "/profile",
];

const TIER1_PREFIXES = ["/return-exchange-policy"];

const TIER2_PREFIXES = [
  "/orders",
  "/delivery-prices",
  "/inventory",
  "/promotions",
  "/stats",
  "/linked-wholesalers",
];

const WHOLESALE_PREFIXES = ["/wholesale"];

export const SUBSCRIPTION_ROUTE = "/subscription";

export function canAccessDashboardRoute(pathname, _search = "", tier = 0) {
  const path = String(pathname || "");

  if (path === SUBSCRIPTION_ROUTE || path.startsWith(`${SUBSCRIPTION_ROUTE}/`)) {
    return { allowed: true, reason: null };
  }

  if (path === "/setup" || path.startsWith("/setup/")) {
    return { allowed: true, reason: null };
  }

  const matches = (prefixes) =>
    prefixes.some((p) => path === p || path.startsWith(`${p}/`));

  if (matches(BASE_PREFIXES)) {
    return { allowed: tier >= 0, reason: tier >= 0 ? null : "base" };
  }

  if (matches(TIER1_PREFIXES)) {
    return { allowed: tier >= 2, reason: tier >= 2 ? null : "tier2" };
  }

  if (matches(TIER2_PREFIXES)) {
    return { allowed: tier >= 2, reason: tier >= 2 ? null : "tier2" };
  }

  if (matches(WHOLESALE_PREFIXES)) {
    return { allowed: tier >= 4, reason: tier >= 4 ? null : "wholesale" };
  }

  return { allowed: true, reason: null };
}

export function getRouteLockCopy(reason, language = "ar") {
  const ar = language === "ar";
  const upgrade = ar ? "عرض الخطط والترقية" : "View plans & upgrade";

  if (reason === "tier1" || reason === "tier2") {
    return {
      title: ar ? "هذه الميزة غير متاحة في خطتك" : "This feature isn't in your plan",
      body: ar
        ? `خطتك الحالية لا تشمل هذه الصفحة. للوصول إلى الطلبات، العروض، المخزون، أسعار التوصيل، والتقارير — ترقِّ إلى الخطة الاحترافية (${PLAN_PRICES_ILS.professional} ₪) أو أعلى.`
        : `Your current plan doesn't include this page. Upgrade to Professional (${PLAN_PRICES_ILS.professional} ₪) or higher for orders, promotions, inventory, delivery pricing, and reports.`,
      ctaLabel: upgrade,
    };
  }

  if (reason === "basic_inquiry") {
    return {
      title: ar ? "متاحة في الخطة الاحترافية" : "Available on Professional",
      body: ar
        ? `الخطة الأساسية تشمل السلة وإرسال الطلب عبر واتساب. لإدارة الطلبات، المخزون، العروض، التقارير، وفورم الطلب الكامل — ترقِّ إلى الخطة الاحترافية (${PLAN_PRICES_ILS.professional} ₪) أو أعلى.`
        : `The Basic plan includes cart and WhatsApp order handoff. For order management, inventory, promotions, reports, and full checkout forms — upgrade to Professional (${PLAN_PRICES_ILS.professional} ₪) or higher.`,
      ctaLabel: upgrade,
    };
  }

  if (reason === "wholesale") {
    return {
      title: ar ? "تجار الجملة — خطة مخصصة" : "Wholesale — dedicated plan",
      body: ar
        ? `إدارة تجار الجملة، أسعار الجملة، وطلبيات B2B متاحة فقط ضمن خطة تجار الجملة (${PLAN_PRICES_ILS.wholesale} ₪).`
        : `Wholesale dealers, B2B pricing, and wholesale orders are only available on the Wholesale plan (${PLAN_PRICES_ILS.wholesale} ₪).`,
      ctaLabel: upgrade,
    };
  }

  return {
    title: ar ? "ترقية مطلوبة" : "Upgrade required",
    body: ar
      ? "خطتك الحالية لا تشمل هذه الصفحة. راجع خطط الاشتراك واختر الباقة المناسبة لاحتياجاتك."
      : "Your current plan doesn't include this page. Review subscription plans and pick the right tier.",
    ctaLabel: upgrade,
  };
}

export function getExpiredSubscriptionCopy(language = "ar") {
  const ar = language === "ar";
  return {
    title: ar ? "انتهى اشتراكك" : "Your subscription has expired",
    body: ar
      ? "اشتراكك الحالي منتهٍ. جدّد الاشتراك للاستمرار في إدارة متجرك والاستفادة من جميع المزايا."
      : "Your subscription has ended. Renew to keep managing your store and access your features.",
    ctaLabel: ar ? "تجديد الاشتراك" : "Renew subscription",
  };
}

/**
 * Sidebar "Show all + lock" — returns lock reason or null if the nav item is usable.
 * Free (0) & Basic (1): products, settings, profile, subscription, catalog only.
 * Professional (3, includes legacy 80₪ professional): all except wholesale.
 * Wholesale (4): everything.
 */
export function getSidebarNavLockReason(itemId, tier = 0) {
  const id = String(itemId || "");
  const t = Number(tier);
  const safeTier = Number.isFinite(t) && t >= 0 ? t : 0;

  const alwaysOpen =
    id === "products_group" ||
    id === "settings_group" ||
    id.startsWith("products_tab_") ||
    id.startsWith("settings_") ||
    id === "profile" ||
    id === "subscription" ||
    id === "catalog";
  if (alwaysOpen) return null;

  if (id === "wholesale_group" || id.startsWith("wholesale_")) {
    return safeTier >= 4 ? null : "wholesale";
  }

  const proTierItems = new Set([
    "orders",
    "linked_wholesalers",
    "delivery_prices",
    "return_exchange_policy",
    "inventory",
    "promotions",
    "stats",
  ]);
  if (proTierItems.has(id)) {
    return safeTier >= 2 ? null : "basic_inquiry";
  }

  return null;
}

/** Stats widgets: merged professional (tier 3+) = full (incl. cost/net profit, AI). */
export function canAccessStatsFeature(tier, featureKey) {
  if (tier >= 3) return true;

  if (tier >= 2) {
    return [
      "whatsapp_clicks",
      "active_products",
      "total_sales",
      "visitor_traffic_7d",
      "total_visitors",
      "total_orders",
      "confirmed_orders",
      "top_cities",
      "top_sold_and_viewed",
    ].includes(featureKey);
  }

  return false;
}
